/* RESPONSIBLE TEAM: team-phone */
import { type Message } from 'embercom/models/calling-details';
import type CallSummary from './renderable/call-summary';
import { tracked } from '@glimmer/tracking';
import { type CallingDetail } from './call-detail';
import { type CallRecording } from './call-recording';

type SidebarCallDetailsInterface = Pick<
  CallSummary,
  | 'id'
  | 'recordingUrl'
  | 'transcriptUrl'
  | 'csatRating'
  | 'recordingEnabled'
  | 'detailedState'
  | 'type'
  | 'direction'
  | 'duration'
  | 'initiatedAt'
> & {
  additionalCallDetailsLoaded: boolean;
  recordingDeleted: boolean;
  transcript: Message[] | null;
  transcriptStatus: string | null;
  isVideo: boolean;
  hasRecordingError: boolean;
  forwardedTo: string | null;
  workspacePhoneNumber: string | null;
  adminId: number | null;
  finLegRecordingUrl: string | null;
  finLegJsonTranscript: Message[] | null;
  finLegTranscriptUrl: string | null;
  terminatingPartyType: string | null;
  endedAt: Date | null;
};

export default class SidebarCallDetails implements SidebarCallDetailsInterface {
  @tracked id: string;
  @tracked recordings: CallRecording[] = [];
  // Voicemails are also stored in recordings
  @tracked externalId: string | null;
  @tracked recordingUrl: string | null;
  @tracked transcriptUrl: string | null;
  @tracked direction: string;
  @tracked csatRating: string | null;
  @tracked recordingEnabled: boolean;
  @tracked detailedState: string | null;
  @tracked type: string;
  @tracked initiatedAt: Date;
  @tracked duration: number | null;
  @tracked additionalCallDetailsLoaded = false;
  @tracked recordingDeleted = false;
  @tracked transcript: Message[] | null = null;
  @tracked transcriptStatus: string | null = null;
  @tracked isVideo = false;
  @tracked hasRecordingError = false;
  @tracked forwardedTo: string | null = null;
  @tracked workspacePhoneNumber: string | null = null;
  @tracked adminId: number | null = null;
  @tracked finLegRecordingUrl: string | null = null;
  @tracked finLegJsonTranscript: Message[] | null = null;
  @tracked finLegTranscriptUrl: string | null = null;
  @tracked terminatingPartyType: string | null = null;
  @tracked endedAt: Date | null = null;

  constructor(callSummary: CallSummary) {
    this.id = callSummary.id;
    this.externalId = callSummary.externalId;
    this.recordingUrl = callSummary.recordingUrl;
    this.transcriptUrl = callSummary.transcriptUrl;
    this.direction = callSummary.direction;
    this.csatRating = callSummary.csatRating;
    this.recordingEnabled = callSummary.recordingEnabled;
    this.detailedState = callSummary.detailedState;
    this.type = callSummary.type;
    this.direction = callSummary.direction;
    this.initiatedAt = callSummary.initiatedAt;
    this.duration = callSummary.duration;
    // Initialize fin leg properties - these will be set when addCallDetails is called
    this.finLegRecordingUrl = null;
    this.finLegJsonTranscript = null;
    this.finLegTranscriptUrl = null;
  }

  async addCallDetails(callDetails: CallingDetail) {
    let isVideo = false;
    let hasRecordingError = false;

    this.recordingDeleted = callDetails.recordingName === 'deleted';

    if (this.recordingUrl && !this.recordingDeleted) {
      ({ isVideo, hasRecordingError } = await this.fileHasVideo(this.recordingUrl));
    }

    this.adminId = callDetails.adminId;
    this.isVideo = isVideo;
    this.hasRecordingError = hasRecordingError;
    this.forwardedTo = callDetails.forwardedTo;
    this.workspacePhoneNumber = callDetails.workspacePhoneNumber;
    this.transcript = this.formatTranscriptData(callDetails.jsonTranscript);
    this.transcriptStatus = callDetails.transcriptStatus;

    // Handle fin leg data
    this.finLegRecordingUrl = callDetails.finLegRecordingUrl;
    this.finLegJsonTranscript = this.formatTranscriptData(callDetails.finLegJsonTranscript);
    this.finLegTranscriptUrl = callDetails.finLegTranscriptUrl;

    // Handle terminating party type
    this.terminatingPartyType = callDetails.terminatingPartyType;

    // Handle ended at timestamp
    this.endedAt = callDetails.endedAt ? new Date(callDetails.endedAt) : null;

    // Process recordings array and format transcripts
    this.recordings = await this.processRecordings(callDetails.recordings ?? []);

    this.additionalCallDetailsLoaded = true;
  }

  private async processRecordings(recordings: CallRecording[] | null): Promise<CallRecording[]> {
    if (!recordings) {
      return [];
    }

    return await Promise.all(
      recordings.map(async (recording) => {
        let isVideo = false;
        let hasRecordingError = false;
        if (recording.recordingUrl) {
          ({ isVideo, hasRecordingError } = await this.fileHasVideo(recording.recordingUrl));
        }
        return {
          ...recording,
          jsonTranscript: this.formatTranscriptData(recording.jsonTranscript),
          isVideo,
          hasRecordingError,
        };
      }),
    );
  }

  get mainRecordings(): CallRecording[] {
    return this.recordings.filter((r) => r.recordingType === 'call');
  }

  get finRecordings(): CallRecording[] {
    return this.recordings.filter((r) => r.recordingType === 'fin_voice');
  }

  private formatTranscriptData(originalTranscript: Message[] | null): Message[] {
    if (originalTranscript?.length && this.transcriptHasContent(originalTranscript)) {
      let newTranscript = structuredClone(originalTranscript);
      newTranscript.map((message: Message) => {
        let totalSeconds: number = parseFloat(message.start_time);
        let minutes: number = Math.floor(totalSeconds / 60);
        let seconds: number = Math.floor(totalSeconds % 60);

        message.start_time = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        return message;
      });
      return newTranscript;
    }
    return [];
  }

  private transcriptHasContent(transcript: Message[]) {
    return Object.keys(transcript[0]).length > 0;
  }

  private async fileHasVideo(
    path: string,
  ): Promise<{ isVideo: boolean; hasRecordingError: boolean }> {
    return new Promise<{ isVideo: boolean; hasRecordingError: boolean }>((resolve) => {
      let video = document.createElement('video');
      video.src = path;

      let errorHandler: ((error: ErrorEvent) => void) | null = null;
      let loadHandler: (() => void) | null = null;

      let clearHandlers = () => {
        if (errorHandler) {
          video.removeEventListener('error', errorHandler);
        }
        if (loadHandler) {
          video.removeEventListener('loadedmetadata', loadHandler);
        }
      };

      errorHandler = (error) => {
        clearHandlers();

        console.error('Failed to load recording', error.message);
        resolve({ hasRecordingError: true, isVideo: false });
      };

      loadHandler = () => {
        clearHandlers();
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          resolve({ hasRecordingError: false, isVideo: true });
        } else {
          resolve({ hasRecordingError: false, isVideo: false });
        }
      };

      video.addEventListener('error', errorHandler);
      video.addEventListener('loadedmetadata', loadHandler);
    });
  }
}
